import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountCircle as UsersIcon,
  Groups as GroupsIcon,
  AccountTree as AccountTreeIcon,
  Policy as PolicyIcon,
  Storage as StorageIcon,
  Schema as SchemaIcon,
  HealthAndSafety as DiagnosticsIcon,
  Dns as AgentsIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Cable as SSHIcon,
  AdminPanelSettings as WebUIUsersIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  ExpandLess,
  ExpandMore,
  Download as InstallersIcon,
  MenuBook as GuideIcon,
  Hub as IntegrationsIcon,
  PlaylistAddCheck as BulkIcon,
} from '@mui/icons-material';
import { logout } from '../store/authSlice';
import { toggleTheme } from '../store/themeSlice';
import { authAPI } from '../services/api';
import SudoPrompt from './SudoPrompt';
import AgentSelector from './AgentSelector';
import { agentsAPI } from '../services/api';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Users', icon: <UsersIcon />, path: '/users' },
  { text: 'Groups', icon: <GroupsIcon />, path: '/groups' },
  { text: 'OUs', icon: <AccountTreeIcon />, path: '/ous' },
  { text: 'Group Policy', icon: <PolicyIcon />, path: '/gpo' },
  { text: 'LDAP', icon: <StorageIcon />, path: '/ldap' },
  { text: 'Schema', icon: <SchemaIcon />, path: '/schema' },
  { text: 'Diagnostics', icon: <DiagnosticsIcon />, path: '/diagnostics' },
  // The control plane for the DCs this app manages. Top-level, not under
  // Settings: it is how you see whether the app can reach a DC at all.
  { text: 'Agents', icon: <AgentsIcon />, path: '/agents' },
  { text: 'Installers', icon: <InstallersIcon />, path: '/installers' },
  // Admin-only: it documents enrollment, installers and trusts, which are all
  // admin actions, and it names operational facts (service list, sudo grants)
  // that are not an operator's business.
  { text: 'Bulk operations', icon: <BulkIcon />, path: '/bulk', adminOnly: true },
  { text: 'Integrations', icon: <IntegrationsIcon />, path: '/integrations', adminOnly: true },
  { text: 'User guide', icon: <GuideIcon />, path: '/guide', adminOnly: true },
];

const settingsMenuItems = [
  { text: 'Email Configuration', icon: <EmailIcon />, path: '/email-config' },
  { text: 'SSH Config', icon: <SSHIcon />, path: '/ssh-config' },
  { text: 'WebUI Users', icon: <WebUIUsersIcon />, path: '/webui-users' },
];

const allMenuItems = [...menuItems, ...settingsMenuItems];

function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sudoPromptOpen, setSudoPromptOpen] = useState(false);
  // Whether the SSH fallback is the live transport. Only THEN does a sudo
  // password mean anything: with an agent serving the DC the app holds no
  // credential for it, so the Sudo button would prompt for something nothing
  // consumes.
  const [sshFallbackActive, setSshFallbackActive] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser);
  const themeMode = useSelector((state) => state.theme.mode);

  useEffect(() => {
    let alive = true;
    const probe = () =>
      agentsAPI
        .getPolicy()
        .then((r) => alive && setSshFallbackActive(Boolean(r.data.ssh_fallback_active)))
        .catch(() => alive && setSshFallbackActive(false));
    probe();
    const id = setInterval(() => {
      if (!document.hidden) probe();
    }, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Silently handle logout errors - user will be logged out locally regardless
    } finally {
      dispatch(logout());
      navigate('/login');
    }
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 3, py: 2 }}>
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            fontWeight: 700,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #22A7F0 0%, #FF8A3D 100%)'
              : 'linear-gradient(135deg, #0877A6 0%, #FF6E00 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Samba4 Web UI
        </Typography>
      </Toolbar>
      <List sx={{ flex: 1, px: 1, py: 1 }}>
        {menuItems.filter((i) => !i.adminOnly || isAdmin).map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                minHeight: 44,
                borderRadius: 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: location.pathname === item.path ? 600 : 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding sx={{ mt: 1, mb: 0.5 }}>
          <ListItemButton
            onClick={() => setSettingsOpen(!settingsOpen)}
            sx={{
              minHeight: 44,
              borderRadius: 2,
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            />
            {settingsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 1 }}>
            {settingsMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  sx={{
                    pl: 3,
                    minHeight: 40,
                    borderRadius: 2,
                  }}
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.8125rem',
                      fontWeight: location.pathname === item.path ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </List>
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
          {user?.username || 'User'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        color="primary"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            {allMenuItems.find((item) => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          {/* Which DC everything on screen refers to. Selecting one is what
              triggers loading: it is sent as X-Samba-Agent on every request
              and clears the cached directory data. */}
          <AgentSelector />
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>
              {user?.username || 'User'}
            </Typography>
          </Box>
          <IconButton
            color="inherit"
            onClick={handleThemeToggle}
            aria-label="Toggle theme"
            sx={{
              mr: { xs: 0.5, sm: 1 },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          {/* The Sudo button is a relic of the SSH transport: it collects a password
              used to run samba-tool over SSH. With an agent serving the DC the app
              holds no credential for that host at all, so the prompt would ask for
              something nothing consumes. Shown only when the SSH fallback is
              actually the live transport. */}
          {sshFallbackActive && (
            <Button
            color="inherit"
            onClick={() => setSudoPromptOpen(true)}
            sx={{
              mr: { xs: 0.5, sm: 1 },
              display: { xs: 'none', sm: 'inline-flex' },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
            aria-label="Sudo authentication"
          >
            Sudo
          </Button>
          )}
          <IconButton
            color="inherit"
            onClick={handleLogout}
            aria-label="Logout"
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 8, sm: 9 },
          minHeight: '100vh',
          // A flex item defaults to min-width:auto, which floors it at its
          // content's MIN-CONTENT width. One wide child -- a tab strip, a table,
          // a long DN -- therefore widened the whole page and gave the body a
          // horizontal scrollbar at phone width, instead of scrolling inside
          // the element that was actually too wide. minWidth:0 lets `main`
          // shrink to the viewport and pushes the overflow back down to the
          // child that owns it.
          minWidth: 0,
          // `transition: all` animates layout properties (width/padding), which
          // runs on the layout thread and janks on every drawer toggle. Only
          // transform and opacity belong in a transition.
          transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <Outlet />
      </Box>
      <SudoPrompt open={sudoPromptOpen} onClose={() => setSudoPromptOpen(false)} />
    </Box>
  );
}

export default Layout;
